"use client";

/**
 * HorizontalScroll — wraps a horizontally scrollable row (category pills,
 * subcategories, timeframe chips, filter tabs) with affordances that
 * signal "there's more to the side":
 *
 *   - Edge fade gradients that blend into the page bg; only rendered
 *     when there's actually hidden content in that direction.
 *   - Chevron buttons on each side that scroll programmatically; only
 *     visible when the corresponding edge has overflow. Click scrolls
 *     by ~70% of the container width.
 *
 * Usage:
 *   <HorizontalScroll>
 *     {pills.map(p => <button key={p.id} ...>{p.label}</button>)}
 *   </HorizontalScroll>
 *
 * Reactive: recomputes on mount, on resize, on content change (ResizeObserver),
 * and while the user scrolls. Bails out gracefully if ResizeObserver isn't
 * supported (still works on initial render + scroll).
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState, ReactNode, type MouseEventHandler } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Extra classes applied to the scroll viewport (where flex children live). */
  className?: string;
  /** Classes applied to the wrapper (positioning / margins etc). */
  wrapperClassName?: string;
  /** Fade gradient color — should match the parent bg. */
  fadeFrom?: string;
  /** Scroll step as a fraction of the visible width (0–1). Default 0.7. */
  scrollStep?: number;
  /** Chevron button size (px). Default 24. */
  chevronSize?: number;
  /** Role on the scroll container (e.g. "tablist"). */
  role?: string;
  /** aria-label on the scroll container. */
  ariaLabel?: string;
  /** Forwarded to the scroll viewport — lets callers keep a drag-to-scroll
   *  handler (grab-drag) on the same element the ref points at. */
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
}

/**
 * forwardRef so callers that already have a ref on their scroll row
 * (e.g. useDragScroll) can keep using it. We wire it to the internal
 * scroll container via useImperativeHandle.
 */
const HorizontalScroll = forwardRef<HTMLDivElement, Props>(function HorizontalScroll(
  {
    children,
    className = "",
    wrapperClassName = "",
    fadeFrom = "bg-white dark:bg-[#0f0f12]",
    scrollStep = 0.7,
    chevronSize = 24,
    role,
    ariaLabel,
    onMouseDown,
  },
  externalRef,
) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(externalRef, () => scrollRef.current as HTMLDivElement, []);
  const [hasLeft, setHasLeft] = useState(false);
  const [hasRight, setHasRight] = useState(false);
  // 2026-05-29 — only show the scroll chevrons on hover/fine-pointer
  // (desktop). On touch the chevrons sit on top of the first/last pill and
  // steal the tap (the row scrolls instead of the category/subcategory
  // opening); touch users swipe to scroll, so the chevrons aren't needed.
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const updateAffordance = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setHasLeft((p) => (p === left ? p : left));
    setHasRight((p) => (p === right ? p : right));
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateAffordance();

    const onScroll = () => updateAffordance();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateAffordance);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateAffordance);
      ro.observe(el);
      // also observe children so new pills trigger recompute
      Array.from(el.children).forEach((c) => ro?.observe(c));
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateAffordance);
      ro?.disconnect();
    };
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * scrollStep, behavior: "smooth" });
  };

  return (
    <div className={`relative ${wrapperClassName}`}>
      {/* Edge fades */}
      <div
        aria-hidden
        className={`pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150 ${fadeFrom} [mask-image:linear-gradient(to_right,black,transparent)] ${
          hasLeft ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150 ${fadeFrom} [mask-image:linear-gradient(to_left,black,transparent)] ${
          hasRight ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Chevron buttons — hover/fine-pointer only (see canHover above) */}
      {canHover && hasLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollBy(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300 shadow-sm hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#26262d] transition-colors"
          style={{ width: chevronSize, height: chevronSize }}
        >
          <ChevronLeft size={14} strokeWidth={2.4} />
        </button>
      )}
      {canHover && hasRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollBy(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300 shadow-sm hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#26262d] transition-colors"
          style={{ width: chevronSize, height: chevronSize }}
        >
          <ChevronRight size={14} strokeWidth={2.4} />
        </button>
      )}

      <div
        ref={scrollRef}
        role={role}
        aria-label={ariaLabel}
        onMouseDown={onMouseDown}
        className={`overflow-x-auto overflow-y-hidden scrollbar-hide scroll-smooth ${className}`}
      >
        {children}
      </div>
    </div>
  );
});

export default HorizontalScroll;
